/** @prettier */
import classNames from 'classnames';
import { Map } from 'immutable';
import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import PaymentLogos from '@xanadu/ui-shared/components/PaymentLogos';
import PwrBy from '@xanadu/ui-shared/components/PwrBy';
import Divider from '@xanadu/ui-shared/components/common/Divider';
import ExternalLink from '@xanadu/ui-shared/components/widgets/ExternalLink';
import { MOBILE_BREAKPOINT } from '@xanadu/ui-shared/constants';
import { useReduxBridge } from '@xanadu/ui-shared/contexts/ReduxBridgeContext';
import { useImprovedTranslation } from '@xanadu/ui-shared/hooks';
import { initialiseStaticContent as initialiseStaticContentAct } from '@xanadu/ui-shared/modules/staticContent/actions';
import showLiveSupport from '@xanadu/ui-shared/utils/showLiveSupport';

import {
  startTourMode as startTourModeAct,
  toggleCustomerOnboardingPopup as toggleCustomerOnboardingPopupAct,
} from '../../modules/customerOnboarding/actions';

import { useShrunkLayout } from '../../hooks';

import { getClientFamilyIfAny, getClientSocialLink } from '../../utils';

import FacebookLogo from '../../../../images/footer/facebook.svg';
import InstaLogo from '../../../../images/footer/instagram.svg';
import XLogo from '../../../../images/footer/x.svg';
import YTLogo from '../../../../images/footer/youtube.svg';
import MailIcon from '../../../../images/icons/misc/icon-email.svg';
import LiveChatIcon from '../../../../images/icons/misc/icon-livechat.svg';
import TourIcon from '../../../../images/icons/misc/icon-tour.svg';
import SBCLogo from '../../../../images/various/SBCAwards26.png';

import styles from './ContentFooter.module.scss';

const EMPTY_MAP = new Map();

const FOOTER_WIDTH_THRESHOLD = 878;

const ContentFooter = ({
  widthSize = 0,
  staticContent = EMPTY_MAP,
  initialiseStaticContent,
  startTourMode,
  toggleCustomerOnboardingPopup,
}) => {
  const { t } = useImprovedTranslation();
  const {
    clientName = '',
    clientSite = '',
    uiVersion = '',
    clientSupportEmail = '',
    customization: {
      ContentFooter: {
        displayedSections = [],
        showPoweredBy,
        RegLogos = () => <div />,
        childrenProps = {},
        TopFooterBanner = () => <div />,
        paymentProvidersList = [],
        internationalPartnersLogo = [],
      } = {},
    } = {},
    featuresFlags: {
      liveChatEnabled,
      cashierEnabled,
      displayAffiliateProgram = false,
      enableTakeTour = true,
    } = {},
  } = useReduxBridge() || {};

  const footerRef = useRef(null);

  const shrunkLayout = useShrunkLayout({
    widthSize,
    current: footerRef.current,
    THRESHOLD: FOOTER_WIDTH_THRESHOLD,
  });

  const isMobile = widthSize <= MOBILE_BREAKPOINT;

  const pages = staticContent
    .map((content) => content.pageCategory)
    .groupBy((content) => content)
    .keySeq()
    .toArray();

  const grouped = staticContent
    .sortBy((x) => x.order)
    .reduce(
      (obj, page) => {
        if (obj[page.pageCategory]) {
          return {
            ...obj,
            [page.pageCategory]: [...obj[page.pageCategory], page],
          };
        }
        return obj;
      },
      pages.reduce((acc, key) => ({ ...acc, [key]: [] }), {})
    );

  const isThereAnythingToMap = Object.keys(grouped).length > 0;

  const { url: partnerImageUrl } = internationalPartnersLogo[0] || {};

  const onConsentUpdate = (eventData) => {
    const { detail = {} } = eventData;

    const reloadIsNecessary = ['functional', 'analytics', 'performance', 'advertisement'];

    if (reloadIsNecessary.some((category) => !detail.accepted.includes(category))) {
      setTimeout(() => {
        window.location.reload();
      }, 600);
    }
  };

  const onPrivacyPreferenceClick = () => {
    try {
      document.removeEventListener('cookieyes_consent_update', onConsentUpdate);
      document.addEventListener('cookieyes_consent_update', onConsentUpdate);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('OneTrust error', e.message);
    }
  };

  useEffect(() => {
    initialiseStaticContent();

    return () => {
      document.removeEventListener('cookieyes_consent_update', onConsentUpdate);
    };
  }, []);

  const familyElement = getClientFamilyIfAny(clientName, clientSite);

  const fbLink = getClientSocialLink(clientName, clientSite, 'fb');
  const xLink = getClientSocialLink(clientName, clientSite, 'x');
  const instaLink = getClientSocialLink(clientName, clientSite, 'insta');
  const ytLink = getClientSocialLink(clientName, clientSite, 'yt');

  const footerClass = classNames({
    [styles.footerMain]: true,
    [styles['footerMain--shrunk']]: shrunkLayout,
  });

  return (
    <div className={footerClass} ref={footerRef}>
      <TopFooterBanner />
      <div className={styles.mainContent}>
        {Boolean(partnerImageUrl) && (
          <div
            className={styles.advertisement}
            style={{
              backgroundImage: `url(${partnerImageUrl})`,
            }}
          />
        )}
        <Divider className={styles.divider} />
        <div className={styles.regulations}>
          <RegLogos title={t('Safer Gambling')} {...childrenProps.RegLogos} />
        </div>
        <div className={styles.uiVersion}>Version: {uiVersion}</div>
        <div className={styles.lower}>
          {cashierEnabled && (
            <div className={styles.pays}>
              <div>{t('available funding methods')}</div>
              <div className={styles.logosPay}>
                <PaymentLogos paymentProvidersList={paymentProvidersList} />
              </div>
            </div>
          )}
          <div className={styles.social}>
            <div>{t('follow us on social media')}</div>
            <div className={styles.socialsLogos}>
              {fbLink ? (
                <ExternalLink href={fbLink}>
                  <FacebookLogo />
                </ExternalLink>
              ) : null}
              {xLink ? (
                <ExternalLink href={xLink}>
                  <XLogo />
                </ExternalLink>
              ) : null}
              {instaLink ? (
                <ExternalLink href={instaLink}>
                  <InstaLogo />
                </ExternalLink>
              ) : null}
              {ytLink ? (
                <ExternalLink href={ytLink}>
                  <YTLogo />
                </ExternalLink>
              ) : null}
            </div>
          </div>
        </div>
        <div className={styles.lowest}>
          <div>{t('Get Help')}</div>
          {liveChatEnabled && (
            <div>
              <LiveChatIcon />
              <a
                role="button"
                onClick={() => {
                  showLiveSupport();
                }}
              >
                <span>{`${t('Customer Support')} - ${t('Live Chat')}`}</span>
              </a>
            </div>
          )}
          <div>
            <MailIcon className={styles.mail} />
            <a href={`mailto:${clientSupportEmail}`}>
              <span>{clientSupportEmail}</span>
            </a>
          </div>
          {enableTakeTour && (
            <div>
              <TourIcon />
              <a
                role="button"
                onClick={() => {
                  // TODO: move this to saga level to avoid polluting the component
                  if (isMobile) {
                    toggleCustomerOnboardingPopup({
                      visible: true,
                      firstTimeVisitor: false,
                      forceTour: true,
                    });
                  } else {
                    startTourMode();
                  }
                }}
              >
                <span>{t('Take a Tour')}</span>
              </a>
            </div>
          )}
          {showPoweredBy ? (
            <PwrBy className={styles.specialOne} familyElement={familyElement} />
          ) : null}
          <img src={SBCLogo} alt="SBC Awards Logo" className={styles.sbcLogo} />
        </div>
      </div>
      <div className={styles.pennant} />
      <div className={styles.links}>
        {displayedSections.map(({ category, 'label-key': labelKey }) => (
          <div key={`section-${category}`} className={styles.linksSection}>
            <span key={`header-${category}`}>{t(labelKey)}</span>
            <div key={`header-${category}-container`}>
              {Boolean(isThereAnythingToMap && grouped[category]) &&
                grouped[category].map((content) => (
                  <React.Fragment key={`frag_${content.title}`}>
                    <Link
                      key={`link_to_${content.title}`}
                      className={styles.footerLink}
                      to={`/page/${category}/${content.urlTitle}`}
                    >
                      {content.title}
                    </Link>
                    <span key={`span_for_${content.title}`} />
                  </React.Fragment>
                ))}
              {displayAffiliateProgram && category === 'about' && (
                <React.Fragment>
                  <Link role="button" className={styles.footerLink} to="/partners/affiliates">
                    {t('Affiliate Program')}
                  </Link>
                  <span />
                </React.Fragment>
              )}
              {category === 'responsible_gaming' && window.cookieyes && (
                <React.Fragment>
                  <a
                    role="button"
                    className={`${styles.footerLink} cky-banner-element`}
                    onClick={onPrivacyPreferenceClick}
                  >
                    {t('Privacy Preference Center')}
                  </a>
                  <span />
                </React.Fragment>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

ContentFooter.propTypes = {
  initialiseStaticContent: PropTypes.func.isRequired,
  startTourMode: PropTypes.func.isRequired,
  toggleCustomerOnboardingPopup: PropTypes.func.isRequired,
  staticContent: PropTypes.instanceOf(Map).isRequired,
  widthSize: PropTypes.number.isRequired,
};

const mapDispatchToProps = {
  initialiseStaticContent: initialiseStaticContentAct,
  startTourMode: startTourModeAct,
  toggleCustomerOnboardingPopup: toggleCustomerOnboardingPopupAct,
};

function mapStateToProps(state) {
  return {
    staticContent: state.get('staticContent'),
    widthSize: state.getIn(['application', 'widthSize']),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(ContentFooter);
